import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, inputCls } from "@/components/kit";
import { api, errMsg, API } from "@/lib/api";
import { useSite } from "@/lib/seo";
import { SectionHead } from "@/pages/manage/common";
import { ImageUpload } from "@/components/ImageUpload";

function useSiteForm() {
  const [f, setF] = useState(null);
  const { reload } = useSite();
  useEffect(() => {
    api.get("/platform/site").then((r) => setF(r.data));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/platform/site", f);
      setF(data);
      reload();
      toast.success("Saved — live on the site now");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const bind = (k) => ({ value: f?.[k] ?? "", onChange: (e) => setF({ ...f, [k]: e.target.value }), "data-testid": `site-${k}-input` });
  return { f, setF, save, bind };
}

const Area = (props) => <textarea className={`${inputCls} h-24`} {...props} />;
const SaveBtn = () => <Button type="submit" className="rounded-full px-8 font-bold uppercase" data-testid="site-save-btn">Save changes</Button>;

export function SiteContent() {
  const { f, setF, save, bind } = useSiteForm();
  if (!f) return null;
  const setFeature = (i, k, v) => setF({ ...f, features: f.features.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-content-form">
      <SectionHead title="Site content" text="Edit the words and images on the public homepage."><SaveBtn /></SectionHead>
      <Panel className="space-y-4">
        <p className="font-display text-xl font-bold uppercase text-white">Announcement bar</p>
        <Field label="Message (leave blank to hide)"><Input {...bind("announcement")} /></Field>
      </Panel>
      <Panel className="space-y-4">
        <p className="font-display text-xl font-bold uppercase text-white">Hero</p>
        <Field label="Small heading"><Input {...bind("hero_overline")} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Headline"><Input {...bind("hero_title")} /></Field>
          <Field label="Headline accent (highlighted)"><Input {...bind("hero_title_accent")} /></Field>
        </div>
        <Field label="Intro text"><Area {...bind("hero_subtitle")} /></Field>
        <Field label="Hero image" hint="Leave empty for the default stadium photo"><ImageUpload value={f.hero_image_url} onChange={(v) => setF({ ...f, hero_image_url: v })} testid="site-hero" wide /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Main button"><Input {...bind("cta_primary")} /></Field>
          <Field label="Second button"><Input {...bind("cta_secondary")} /></Field>
        </div>
      </Panel>
      <Panel className="space-y-4">
        <div className="flex items-center justify-between"><p className="font-display text-xl font-bold uppercase text-white">Features</p>
          <Button type="button" size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => setF({ ...f, features: [...f.features, { title: "", text: "" }] })} data-testid="add-feature-btn"><Plus className="mr-1 h-4 w-4" /> Add</Button></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Small heading"><Input {...bind("features_overline")} /></Field>
          <Field label="Section title"><Input {...bind("features_title")} /></Field>
        </div>
        {f.features.map((x, i) => (
          <div key={i} className="flex gap-3 rounded-md border border-white/10 p-3" data-testid={`feature-row-${i}`}>
            <div className="flex-1 space-y-2">
              <Input value={x.title} onChange={(e) => setFeature(i, "title", e.target.value)} placeholder="Title" data-testid={`feature-title-${i}`} />
              <Input value={x.text} onChange={(e) => setFeature(i, "text", e.target.value)} placeholder="Description" data-testid={`feature-text-${i}`} />
            </div>
            <Button type="button" size="icon" variant="ghost" className="text-rose-400" onClick={() => setF({ ...f, features: f.features.filter((_, j) => j !== i) })} data-testid={`remove-feature-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </Panel>
      <Panel className="space-y-4">
        <p className="font-display text-xl font-bold uppercase text-white">Roles, pricing & footer</p>
        <Field label="Roles small heading"><Input {...bind("roles_overline")} /></Field>
        <Field label="Roles title"><Input {...bind("roles_title")} /></Field>
        <Field label="Roles text"><Area {...bind("roles_text")} /></Field>
        {(f.roles || []).map((x, i) => (
          <div key={i} className="space-y-2 rounded-md border border-white/10 p-3" data-testid={`role-row-${i}`}>
            <Input value={x.title} onChange={(e) => setF({ ...f, roles: f.roles.map((r, j) => (j === i ? { ...r, title: e.target.value } : r)) })} placeholder="Role" data-testid={`role-title-${i}`} />
            <Input value={x.text} onChange={(e) => setF({ ...f, roles: f.roles.map((r, j) => (j === i ? { ...r, text: e.target.value } : r)) })} placeholder="Description" data-testid={`role-text-${i}`} />
          </div>
        ))}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Pricing small heading"><Input {...bind("pricing_overline")} /></Field>
          <Field label="Plan button text"><Input {...bind("pricing_cta")} /></Field>
          <Field label="Pricing title"><Input {...bind("pricing_title")} /></Field>
          <Field label="Pricing text"><Input {...bind("pricing_text")} /></Field>
        </div>
        <Field label="Team directory heading"><Input {...bind("directory_title")} /></Field>
        <Field label="Footer text"><Input {...bind("footer_text")} /></Field>
      </Panel>
      <Panel className="space-y-4">
        <p className="font-display text-xl font-bold uppercase text-white">Stripe checkout wording</p>
        <Field label="Subscription product name" hint="Shown on Stripe checkout and receipts. {plan} is replaced with the plan name."><Input {...bind("billing_product_name")} /></Field>
      </Panel>
      <Panel className="space-y-4">
        <p className="font-display text-xl font-bold uppercase text-white">Partners in Travel</p>
        <p className="text-sm text-slate-400">Shown as a Travel page on team hubs you enable (Teams tab) or that sign up with a partner code.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Section title"><Input {...bind("travel_title")} /></Field>
          <Field label="Partner name"><Input {...bind("travel_partner")} /></Field>
        </div>
        <Field label="Text"><Area {...bind("travel_text")} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Button text"><Input {...bind("travel_cta")} /></Field>
          <Field label="Partner website (optional)"><Input {...bind("travel_link")} placeholder="https://…" /></Field>
        </div>
        <Field label="Partner / coupon codes" hint="Comma separated. Teams signing up with one get the Travel page."><Input {...bind("travel_codes")} placeholder="NJT, TRAVEL26" /></Field>
        {(f.travel_codes || "").split(",").filter((c) => c.trim()).map((c) => <p key={c} className="break-all font-mono text-xs text-slate-400" data-testid="travel-code-link">{`${window.location.origin}/signup?code=${c.trim()}`}</p>)}
      </Panel>
    </form>
  );
}

export function SeoSettings() {
  const { f, setF, save, bind } = useSiteForm();
  if (!f) return null;
  const sitemap = `${API}/public/sitemap.xml?origin=${encodeURIComponent(window.location.origin)}`;
  return (
    <form onSubmit={save} className="space-y-6" data-testid="seo-form">
      <SectionHead title="SEO & branding" text="Browser tab title, search snippet, favicon and social share image."><SaveBtn /></SectionHead>
      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <Panel className="space-y-4">
          <Field label="Site name"><Input {...bind("site_name")} /></Field>
          <Field label="Page title" hint="Shown in the browser tab and Google results (aim for under 60 characters)"><Input {...bind("seo_title")} /></Field>
          <Field label="Meta description" hint={`${(f.meta_description || "").length}/160 characters recommended`}><Area {...bind("meta_description")} /></Field>
          <Field label="Favicon" hint="Square PNG or ICO, e.g. 64×64"><ImageUpload value={f.favicon_url} onChange={(v) => setF({ ...f, favicon_url: v })} testid="seo-favicon" contain /></Field>
          <Field label="Social share image" hint="1200×630 image used when links are shared"><ImageUpload value={f.og_image_url} onChange={(v) => setF({ ...f, og_image_url: v })} testid="seo-og-image" wide /></Field>
        </Panel>
        <div className="space-y-6">
          <Panel data-testid="seo-preview">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Google preview</p>
            <div className="rounded-md bg-white p-4">
              <div className="flex items-center gap-2">{f.favicon_url && <img src={f.favicon_url} alt="favicon" className="h-5 w-5" />}<span className="text-xs text-[#202124]">{window.location.host}</span></div>
              <p className="mt-1 text-lg text-[#1a0dab]">{f.seo_title}</p>
              <p className="text-sm text-[#4d5156]">{f.meta_description}</p>
            </div>
          </Panel>
          <Panel>
            <p className="text-sm text-slate-300">Sitemap for search engines (homepage, custom pages and every live team hub):</p>
            <a href={sitemap} target="_blank" rel="noreferrer" className="mt-2 block break-all font-mono text-xs text-[#CCFF00]" data-testid="sitemap-link">{sitemap}</a>
            <p className="mt-3 text-xs text-slate-500">Team hubs automatically use the team name and tagline for their title and description.</p>
          </Panel>
        </div>
      </div>
    </form>
  );
}
